import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  alpha,
} from '@mui/material';
import {
  Hub as NetworkIcon,
  AccountTree as TreeIcon,
  ZoomIn as ZoomIcon,
  GridOn as HeatmapIcon,
  BubbleChart as PackIcon,
  Analytics as AnnotationIcon,
  Timeline as TimelineIcon,
  BarChart as BarIcon,
  DonutLarge as DonutIcon,
} from '@mui/icons-material';
import { Group } from '@visx/group';
import { Tree, hierarchy } from '@visx/hierarchy';
import { LinkHorizontal } from '@visx/shape';
import { LinearGradient } from '@visx/gradient';
import { Zoom } from '@visx/zoom';
import { RectClipPath } from '@visx/clip-path';
import { scaleLinear, scaleBand } from '@visx/scale';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { GridRows, GridColumns } from '@visx/grid';
import { HeatmapRect } from '@visx/heatmap';
import { Pack } from '@visx/hierarchy';
import { Annotation, Label, Connector, CircleSubject } from '@visx/annotation';
import { Pie } from '@visx/shape';
import { AreaClosed, LinePath, Bar } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { Text } from '@visx/text';

const visxColors = {
  primary: '#0078D4',
  secondary: '#40E0D0',
  success: '#107C10',
  warning: '#FFB900',
  danger: '#D13438',
  purple: '#5C2D91',
  chart: ['#0078D4', '#40E0D0', '#107C10', '#FFB900', '#5C2D91', '#D13438', '#FF8C00', '#E91E63'],
};

// Data generators matching Recharts showcase
const generateTimeSeriesData = (days = 30) => {
  return Array.from({ length: days }, (_, i) => ({
    date: `Day ${i + 1}`,
    index: i,
    conversations: Math.floor(Math.random() * 100 + 50),
    answered: Math.floor(Math.random() * 70 + 40),
    unanswered: Math.floor(Math.random() * 30 + 10),
    messages: Math.floor(Math.random() * 300 + 150),
    responseTime: Math.floor(Math.random() * 20 + 5),
  }));
};

const channelData = [
  { name: 'WhatsApp', value: 580, color: visxColors.success },
  { name: 'Email', value: 220, color: visxColors.primary },
  { name: 'Web Chat', value: 120, color: visxColors.secondary },
  { name: 'SMS', value: 80, color: visxColors.warning },
];

const stackedBarData = [
  { channel: 'WhatsApp', human: 280, automation: 420 },
  { channel: 'Email', human: 90, automation: 160 },
  { channel: 'Web', human: 120, automation: 210 },
  { channel: 'SMS', human: 40, automation: 90 },
];

const scatterData = [
  { responseTime: 2.5, csat: 92, volume: 40 },
  { responseTime: 4.2, csat: 88, volume: 60 },
  { responseTime: 6.1, csat: 82, volume: 80 },
  { responseTime: 8.5, csat: 75, volume: 100 },
  { responseTime: 12.3, csat: 68, volume: 50 },
  { responseTime: 15.8, csat: 62, volume: 30 },
  { responseTime: 3.2, csat: 90, volume: 70 },
  { responseTime: 5.5, csat: 85, volume: 90 },
];

// Tree data
const treeData = {
  name: 'Organization',
  children: [
    {
      name: 'Customer Success',
      children: [
        { name: 'Support L1', value: 320 },
        { name: 'Support L2', value: 180 },
        { name: 'Onboarding', value: 220 },
      ]
    },
    {
      name: 'Sales',
      children: [
        { name: 'Inbound', value: 280 },
        { name: 'Outbound', value: 190 },
        { name: 'Enterprise', value: 140 },
      ]
    },
    {
      name: 'Product',
      children: [
        { name: 'Features', value: 160 },
        { name: 'Bugs', value: 90 },
        { name: 'Research', value: 120 },
      ]
    }
  ]
};

// Network graph data
const networkNodes = [
  { id: 'whatsapp', x: 200, y: 150, size: 40 },
  { id: 'email', x: 350, y: 120, size: 30 },
  { id: 'web', x: 500, y: 160, size: 25 },
  { id: 'support', x: 280, y: 280, size: 35 },
  { id: 'sales', x: 420, y: 300, size: 28 },
  { id: 'product', x: 350, y: 220, size: 22 },
];

const networkLinks = [
  { source: 0, target: 3 },
  { source: 0, target: 4 },
  { source: 1, target: 3 },
  { source: 1, target: 4 },
  { source: 2, target: 3 },
  { source: 2, target: 5 },
  { source: 3, target: 5 },
  { source: 4, target: 5 },
];

// Heatmap data
const heatmapData = Array.from({ length: 7 }, (_, day) => ({
  bin: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][day],
  bins: Array.from({ length: 24 }, (_, hour) => ({
    bin: hour,
    count: Math.floor(Math.random() * 50 + 10),
  })),
}));

// Circle packing data
const packData = {
  name: 'root',
  children: [
    {
      name: 'North America',
      children: [
        { name: 'USA', size: 620 },
        { name: 'Canada', size: 180 },
        { name: 'Mexico', size: 240 },
      ]
    },
    {
      name: 'South America',
      children: [
        { name: 'Argentina', size: 420 },
        { name: 'Chile', size: 220 },
        { name: 'Colombia', size: 180 },
      ]
    },
    {
      name: 'Europe',
      children: [
        { name: 'Spain', size: 280 },
        { name: 'UK', size: 160 },
      ]
    }
  ]
};

const ChartCard = ({ title, subtitle, children, icon: Icon, gradientColors }) => {
  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        background: gradientColors 
          ? `linear-gradient(135deg, ${alpha(gradientColors[0], 0.05)} 0%, ${alpha(gradientColors[1], 0.05)} 100%)`
          : '#FFFFFF',
        borderRadius: 3,
        border: `1px solid ${alpha(visxColors.primary, 0.12)}`,
        boxShadow: '0 8px 32px rgba(0, 120, 212, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: gradientColors
            ? `linear-gradient(90deg, ${gradientColors[0]}, ${gradientColors[1]})`
            : `linear-gradient(90deg, ${visxColors.primary}, ${visxColors.secondary})`,
        },
        '&:hover': {
          boxShadow: '0 16px 48px rgba(0, 120, 212, 0.12)',
          transform: 'translateY(-4px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {Icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: gradientColors
                ? `linear-gradient(135deg, ${alpha(gradientColors[0], 0.15)}, ${alpha(gradientColors[1], 0.15)})`
                : `linear-gradient(135deg, ${alpha(visxColors.primary, 0.15)}, ${alpha(visxColors.secondary, 0.15)})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 24, color: gradientColors?.[0] || visxColors.primary }} />
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
};

const VisxShowcase = () => {
  const timeSeriesData = generateTimeSeriesData(14);

  // Line Chart Component
  const LineChart = ({ width = 600, height = 350 }) => {
    const margin = { top: 20, right: 30, bottom: 40, left: 50 };
    const xMax = width - margin.left - margin.right;
    const yMax = height - margin.top - margin.bottom;

    const xScale = useMemo(
      () =>
        scaleBand({
          domain: timeSeriesData.map(d => d.date),
          range: [0, xMax],
          padding: 0.1,
        }),
      [xMax]
    );

    const yScale = useMemo(
      () =>
        scaleLinear({
          domain: [0, Math.max(...timeSeriesData.map(d => d.conversations))],
          range: [yMax, 0],
          nice: true,
        }),
      [yMax]
    );

    return (
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          <GridRows scale={yScale} width={xMax} strokeDasharray="3,3" stroke={alpha('#000', 0.1)} />
          <AxisBottom 
            top={yMax} 
            scale={xScale} 
            tickLabelProps={() => ({ fontSize: 10, textAnchor: 'middle' })}
          />
          <AxisLeft 
            scale={yScale} 
            tickLabelProps={() => ({ fontSize: 11, textAnchor: 'end', dx: -5 })}
          />
          <LinePath
            data={timeSeriesData}
            x={d => (xScale(d.date) || 0) + xScale.bandwidth() / 2}
            y={d => yScale(d.conversations) || 0}
            stroke={visxColors.primary}
            strokeWidth={3}
            curve={curveMonotoneX}
          />
          <LinePath
            data={timeSeriesData}
            x={d => (xScale(d.date) || 0) + xScale.bandwidth() / 2}
            y={d => yScale(d.answered) || 0}
            stroke={visxColors.success}
            strokeWidth={2}
            curve={curveMonotoneX}
          />
          {timeSeriesData.map((d, i) => (
            <circle
              key={i}
              cx={(xScale(d.date) || 0) + xScale.bandwidth() / 2}
              cy={yScale(d.conversations) || 0}
              r={4}
              fill={visxColors.primary}
            />
          ))}
        </Group>
      </svg>
    );
  };

  // Area Chart Component
  const AreaChart = ({ width = 600, height = 350 }) => {
    const margin = { top: 20, right: 30, bottom: 40, left: 50 };
    const xMax = width - margin.left - margin.right;
    const yMax = height - margin.top - margin.bottom;

    const xScale = useMemo(
      () =>
        scaleBand({
          domain: timeSeriesData.map(d => d.date),
          range: [0, xMax],
        }),
      [xMax]
    );

    const yScale = useMemo(
      () =>
        scaleLinear({
          domain: [0, Math.max(...timeSeriesData.map(d => d.answered + d.unanswered))],
          range: [yMax, 0],
          nice: true,
        }),
      [yMax]
    );

    return (
      <svg width={width} height={height}>
        <LinearGradient id="area-gradient-answered" from={visxColors.primary} to={visxColors.primary} fromOpacity={0.8} toOpacity={0.1} />
        <LinearGradient id="area-gradient-unanswered" from={visxColors.danger} to={visxColors.danger} fromOpacity={0.8} toOpacity={0.1} />
        <Group left={margin.left} top={margin.top}>
          <GridRows scale={yScale} width={xMax} strokeDasharray="3,3" stroke={alpha('#000', 0.1)} />
          <AxisBottom 
            top={yMax} 
            scale={xScale} 
            tickLabelProps={() => ({ fontSize: 10, textAnchor: 'middle' })}
          />
          <AxisLeft 
            scale={yScale} 
            tickLabelProps={() => ({ fontSize: 11, textAnchor: 'end', dx: -5 })}
          />
          <AreaClosed
            data={timeSeriesData}
            x={d => (xScale(d.date) || 0) + xScale.bandwidth() / 2}
            y={d => yScale(d.answered) || 0}
            yScale={yScale}
            fill="url(#area-gradient-answered)"
            stroke={visxColors.primary}
            strokeWidth={2}
            curve={curveMonotoneX}
          />
          <AreaClosed
            data={timeSeriesData}
            x={d => (xScale(d.date) || 0) + xScale.bandwidth() / 2}
            y={d => yScale(d.answered + d.unanswered) || 0}
            y0={d => yScale(d.answered) || 0}
            yScale={yScale}
            fill="url(#area-gradient-unanswered)"
            stroke={visxColors.danger}
            strokeWidth={2}
            curve={curveMonotoneX}
          />
        </Group>
      </svg>
    );
  };

  // Bar Chart Component
  const BarChart = ({ width = 600, height = 350 }) => {
    const margin = { top: 20, right: 30, bottom: 40, left: 100 };
    const xMax = width - margin.left - margin.right;
    const yMax = height - margin.top - margin.bottom;

    const yScale = useMemo(
      () =>
        scaleBand({
          domain: stackedBarData.map(d => d.channel),
          range: [0, yMax],
          padding: 0.2,
        }),
      [yMax]
    );

    const xScale = useMemo(
      () =>
        scaleLinear({
          domain: [0, Math.max(...stackedBarData.map(d => d.human + d.automation))],
          range: [0, xMax],
          nice: true,
        }),
      [xMax]
    );

    return (
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          <GridColumns scale={xScale} height={yMax} strokeDasharray="3,3" stroke={alpha('#000', 0.1)} />
          <AxisLeft 
            scale={yScale} 
            tickLabelProps={() => ({ fontSize: 12, textAnchor: 'end', dx: -10 })}
          />
          <AxisBottom 
            top={yMax} 
            scale={xScale} 
            tickLabelProps={() => ({ fontSize: 11, textAnchor: 'middle', dy: 10 })}
          />
          {stackedBarData.map((d, i) => (
            <Group key={i}>
              <Bar
                x={0}
                y={yScale(d.channel) || 0}
                width={xScale(d.human)}
                height={yScale.bandwidth()}
                fill={visxColors.primary}
                opacity={0.9}
              />
              <Bar
                x={xScale(d.human)}
                y={yScale(d.channel) || 0}
                width={xScale(d.automation)}
                height={yScale.bandwidth()}
                fill={visxColors.secondary}
                opacity={0.9}
              />
            </Group>
          ))}
        </Group>
      </svg>
    );
  };

  // Pie Chart Component
  const PieChart = ({ width = 400, height = 400 }) => {
    const innerWidth = width;
    const innerHeight = height;
    const radius = Math.min(innerWidth, innerHeight) / 2;
    const centerY = innerHeight / 2;
    const centerX = innerWidth / 2;
    const donutThickness = 80;

    return (
      <svg width={width} height={height}>
        <Group top={centerY} left={centerX}>
          <Pie
            data={channelData}
            pieValue={d => d.value}
            outerRadius={radius}
            innerRadius={radius - donutThickness}
            cornerRadius={3}
            padAngle={0.02}
          >
            {pie => (
              <Group>
                {pie.arcs.map((arc, i) => {
                  const [centroidX, centroidY] = pie.path.centroid(arc);
                  const arcPath = pie.path(arc);
                  const arcFill = channelData[i].color;
                  return (
                    <g key={`arc-${i}`}>
                      <path d={arcPath || ''} fill={arcFill} />
                      <Text
                        x={centroidX}
                        y={centroidY}
                        dy=".33em"
                        fill="white"
                        fontSize={12}
                        textAnchor="middle"
                        fontWeight="bold"
                      >
                        {`${Math.round((arc.data.value / channelData.reduce((a, b) => a + b.value, 0)) * 100)}%`}
                      </Text>
                    </g>
                  );
                })}
              </Group>
            )}
          </Pie>
        </Group>
      </svg>
    );
  };

  // Scatter Chart Component
  const ScatterChart = ({ width = 600, height = 400 }) => {
    const margin = { top: 20, right: 30, bottom: 60, left: 60 };
    const xMax = width - margin.left - margin.right;
    const yMax = height - margin.top - margin.bottom;

    const xScale = useMemo(
      () =>
        scaleLinear({
          domain: [0, 20],
          range: [0, xMax],
        }),
      [xMax]
    );

    const yScale = useMemo(
      () =>
        scaleLinear({
          domain: [50, 100],
          range: [yMax, 0],
        }),
      [yMax]
    );

    const sizeScale = useMemo(
      () =>
        scaleLinear({
          domain: [0, 100],
          range: [5, 15],
        }),
      []
    );

    return (
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          <GridRows scale={yScale} width={xMax} strokeDasharray="3,3" stroke={alpha('#000', 0.1)} />
          <GridColumns scale={xScale} height={yMax} strokeDasharray="3,3" stroke={alpha('#000', 0.1)} />
          <AxisBottom 
            top={yMax} 
            scale={xScale} 
            label="Response Time (minutes)"
            labelOffset={15}
            tickLabelProps={() => ({ fontSize: 11, textAnchor: 'middle', dy: 5 })}
          />
          <AxisLeft 
            scale={yScale} 
            label="CSAT Score"
            labelOffset={40}
            tickLabelProps={() => ({ fontSize: 11, textAnchor: 'end', dx: -5 })}
          />
          {scatterData.map((d, i) => (
            <circle
              key={i}
              cx={xScale(d.responseTime)}
              cy={yScale(d.csat)}
              r={sizeScale(d.volume)}
              fill={visxColors.chart[i % visxColors.chart.length]}
              opacity={0.7}
            />
          ))}
        </Group>
      </svg>
    );
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      p: { xs: 2, md: 4 }
    }}>
      {/* Hero Header */}
      <Box sx={{ mb: 5, textAlign: 'center' }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 800, 
            mb: 2,
            background: `linear-gradient(135deg, ${visxColors.primary} 0%, ${visxColors.purple} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Visx by Airbnb - Complete Showcase
        </Typography>
        <Typography variant="h6" sx={{ color: '#666', fontWeight: 400, mb: 3 }}>
          Low-level D3 building blocks - Comparing with Recharts + Advanced visualizations
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Chip label="Line Charts" color="primary" />
          <Chip label="Area Charts" sx={{ bgcolor: visxColors.secondary, color: 'white' }} />
          <Chip label="Bar Charts" sx={{ bgcolor: visxColors.success, color: 'white' }} />
          <Chip label="Pie Charts" sx={{ bgcolor: visxColors.warning, color: 'white' }} />
          <Chip label="Scatter" sx={{ bgcolor: visxColors.purple, color: 'white' }} />
          <Chip label="Network Graphs" sx={{ bgcolor: visxColors.danger, color: 'white' }} />
        </Box>
      </Box>

      {/* Section 1: Time Series & Trends (matching Recharts) */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Time Series & Trends (Comparable to Recharts)
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Multi-Line Chart"
            subtitle="Conversation trends over 2 weeks"
            icon={TimelineIcon}
            gradientColors={['#667eea', '#764ba2']}
          >
            <LineChart width={600} height={350} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Stacked Area Chart"
            subtitle="Message volume distribution"
            icon={TimelineIcon}
            gradientColors={['#2E3192', '#1BFFFF']}
          >
            <AreaChart width={600} height={350} />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 2: Bar & Pie Charts (matching Recharts) */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Comparative & Distribution Analysis
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Horizontal Bar Chart"
            subtitle="Human vs Automation breakdown by channel"
            icon={BarIcon}
            gradientColors={['#134E5E', '#71B280']}
          >
            <BarChart width={600} height={350} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Donut Chart"
            subtitle="Channel distribution"
            icon={DonutIcon}
            gradientColors={[visxColors.purple, visxColors.secondary]}
          >
            <PieChart width={600} height={350} />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 3: Scatter (matching Recharts) */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Correlation & Patterns
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <ChartCard
            title="Scatter Plot"
            subtitle="Response time vs CSAT correlation (bubble size = volume)"
            icon={TimelineIcon}
            gradientColors={['#f093fb', '#f5576c']}
          >
            <ScatterChart width={900} height={400} />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 4: Hierarchical (Visx Unique) */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Hierarchical Visualizations (Visx Specialty)
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Tree Diagram"
            subtitle="Organization hierarchy with custom links"
            icon={TreeIcon}
            gradientColors={['#667eea', '#764ba2']}
          >
            <svg width={600} height={400}>
              <LinearGradient id="links-gradient" from="#fd9b93" to="#fe6e9e" />
              <Tree
                root={hierarchy(treeData, d => d.children)}
                size={[400, 560]}
                separation={(a, b) => (a.parent === b.parent ? 1 : 0.5) / a.depth}
              >
                {(tree) => (
                  <Group top={20} left={30}>
                    {tree.links().map((link, i) => (
                      <LinkHorizontal
                        key={i}
                        data={link}
                        stroke="url(#links-gradient)"
                        strokeWidth="2"
                        fill="none"
                      />
                    ))}
                    {tree.descendants().map((node, i) => {
                      const width = 120;
                      const height = 30;
                      const isLeaf = !node.children;
                      
                      return (
                        <Group key={i} top={node.x} left={node.y}>
                          <rect
                            width={width}
                            height={height}
                            y={-height / 2}
                            x={-width / 2}
                            fill={isLeaf ? visxColors.success : visxColors.primary}
                            stroke={visxColors.chart[node.depth]}
                            strokeWidth={2}
                            rx={8}
                            opacity={0.9}
                          />
                          <text
                            dy="0.33em"
                            fontSize={11}
                            fontWeight="600"
                            textAnchor="middle"
                            fill="white"
                            style={{ pointerEvents: 'none' }}
                          >
                            {node.data.name}
                          </text>
                        </Group>
                      );
                    })}
                  </Group>
                )}
              </Tree>
            </svg>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Circle Packing"
            subtitle="Nested circles showing regional volumes"
            icon={PackIcon}
            gradientColors={['#f093fb', '#f5576c']}
          >
            <svg width={600} height={400}>
              <LinearGradient id="pack-gradient" from={visxColors.primary} to={visxColors.purple} />
              <Pack root={hierarchy(packData).sum(d => d.size || 0)} size={[600, 400]} padding={3}>
                {(pack) => {
                  const circles = pack.descendants();
                  return (
                    <Group>
                      {circles.map((circle, i) => {
                        if (!circle || circle.r === undefined) return null;
                        return (
                          <circle
                            key={`circle-${i}`}
                            cx={circle.x}
                            cy={circle.y}
                            r={circle.r}
                            fill={circle.depth === 0 ? 'transparent' : 
                                  circle.depth === 1 ? alpha(visxColors.chart[i % visxColors.chart.length], 0.3) :
                                  visxColors.chart[i % visxColors.chart.length]}
                            stroke={circle.depth === 0 ? 'transparent' : 'white'}
                            strokeWidth={circle.depth === 0 ? 0 : 2}
                            opacity={0.9}
                          />
                        );
                      })}
                      {circles.map((circle, i) => {
                        if (!circle || circle.depth === 0 || circle.r < 20) return null;
                        return (
                          <text
                            key={`text-${i}`}
                            x={circle.x}
                            y={circle.y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={Math.min(circle.r / 3, 14)}
                            fontWeight="600"
                            fill={circle.depth === 1 ? '#333' : 'white'}
                            style={{ pointerEvents: 'none' }}
                          >
                            {circle.data.name}
                          </text>
                        );
                      })}
                    </Group>
                  );
                }}
              </Pack>
            </svg>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 5: Interactive */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Interactive Features (Visx Advanced)
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Network Graph"
            subtitle="Channel to department connections"
            icon={NetworkIcon}
            gradientColors={['#134E5E', '#71B280']}
          >
            <svg width={600} height={400}>
              <LinearGradient id="network-gradient" from={visxColors.primary} to={visxColors.secondary} />
              <Group>
                {/* Links */}
                {networkLinks.map((link, i) => (
                  <line
                    key={`link-${i}`}
                    x1={networkNodes[link.source].x}
                    y1={networkNodes[link.source].y}
                    x2={networkNodes[link.target].x}
                    y2={networkNodes[link.target].y}
                    stroke="url(#network-gradient)"
                    strokeWidth={2}
                    opacity={0.6}
                  />
                ))}
                {/* Nodes */}
                {networkNodes.map((node, i) => (
                  <Group key={`node-${i}`}>
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={node.size}
                      fill={visxColors.chart[i % visxColors.chart.length]}
                      stroke="white"
                      strokeWidth={3}
                      opacity={0.9}
                      style={{ cursor: 'pointer' }}
                    />
                    <text
                      x={node.x}
                      y={node.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={10}
                      fontWeight="700"
                      fill="white"
                      style={{ pointerEvents: 'none', textTransform: 'uppercase' }}
                    >
                      {node.id}
                    </text>
                  </Group>
                ))}
              </Group>
            </svg>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Zoom & Pan Demo"
            subtitle="Interactive scatter with zoom controls"
            icon={ZoomIcon}
            gradientColors={['#2E3192', '#1BFFFF']}
          >
            <Zoom
              width={600}
              height={400}
              scaleXMin={0.5}
              scaleXMax={4}
              scaleYMin={0.5}
              scaleYMax={4}
            >
              {(zoom) => (
                <div style={{ position: 'relative' }}>
                  <svg width={600} height={400} style={{ cursor: zoom.isDragging ? 'grabbing' : 'grab' }}>
                    <RectClipPath id="zoom-clip" width={600} height={400} />
                    <rect
                      width={600}
                      height={400}
                      fill={alpha(visxColors.primary, 0.05)}
                      onTouchStart={zoom.dragStart}
                      onTouchMove={zoom.dragMove}
                      onTouchEnd={zoom.dragEnd}
                      onMouseDown={zoom.dragStart}
                      onMouseMove={zoom.dragMove}
                      onMouseUp={zoom.dragEnd}
                      onMouseLeave={() => {
                        if (zoom.isDragging) zoom.dragEnd();
                      }}
                    />
                    <Group transform={zoom.toString()} clipPath="url(#zoom-clip)">
                      <GridRows scale={scaleLinear({ domain: [0, 100], range: [0, 400] })} width={600} numTicks={10} stroke={alpha('#000', 0.1)} />
                      <GridColumns scale={scaleLinear({ domain: [0, 100], range: [0, 600] })} height={400} numTicks={10} stroke={alpha('#000', 0.1)} />
                      {Array.from({ length: 30 }, (_, i) => ({
                        x: Math.random() * 600,
                        y: Math.random() * 400,
                        size: Math.random() * 15 + 5,
                      })).map((point, i) => (
                        <circle
                          key={i}
                          cx={point.x}
                          cy={point.y}
                          r={point.size}
                          fill={visxColors.chart[i % visxColors.chart.length]}
                          opacity={0.7}
                        />
                      ))}
                    </Group>
                  </svg>
                  <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 1 }}>
                    <Chip label={`Zoom: ${zoom.transformMatrix.scaleX.toFixed(2)}x`} size="small" sx={{ bgcolor: 'white' }} />
                    <Chip label="Drag to pan" size="small" sx={{ bgcolor: 'white' }} />
                  </Box>
                </div>
              )}
            </Zoom>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 6: Heatmap */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Density & Patterns
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <ChartCard
            title="Weekly Activity Heatmap"
            subtitle="Hourly patterns across the week with custom color scale"
            icon={HeatmapIcon}
            gradientColors={['#f093fb', '#f5576c']}
          >
            <svg width={900} height={300}>
              <LinearGradient id="heatmap-gradient" from={visxColors.danger} to={visxColors.success} />
              <HeatmapRect
                data={heatmapData}
                xScale={scaleLinear({
                  domain: [0, 24],
                  range: [100, 850],
                })}
                yScale={scaleLinear({
                  domain: [0, 7],
                  range: [20, 260],
                })}
                colorScale={scaleLinear({
                  domain: [0, 60],
                  range: [alpha(visxColors.primary, 0.2), visxColors.danger],
                })}
                binWidth={30}
                binHeight={34}
                gap={2}
              >
                {(heatmap) =>
                  heatmap.map((bins) =>
                    bins.map((bin) => (
                      <rect
                        key={`heatmap-rect-${bin.row}-${bin.column}`}
                        x={bin.x}
                        y={bin.y}
                        width={bin.width}
                        height={bin.height}
                        fill={bin.color}
                        rx={4}
                        opacity={bin.opacity}
                      />
                    ))
                  )
                }
              </HeatmapRect>
              <AxisLeft
                left={90}
                scale={scaleLinear({ domain: [0, 6], range: [20, 260] })}
                tickFormat={(v) => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][v]}
                stroke="white"
                tickStroke="white"
                tickLabelProps={() => ({ fill: 'white', fontSize: 11, textAnchor: 'end', dx: -5 })}
              />
              <AxisBottom
                top={270}
                left={0}
                scale={scaleLinear({ domain: [0, 24], range: [100, 850] })}
                numTicks={12}
                stroke="white"
                tickStroke="white"
                tickLabelProps={() => ({ fill: 'white', fontSize: 10, textAnchor: 'middle', dy: 10 })}
              />
            </svg>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 7: Annotations */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Annotations & Labels
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ChartCard
            title="Custom Annotations"
            subtitle="Highlight key data points with visual markers"
            icon={AnnotationIcon}
            gradientColors={['#667eea', '#764ba2']}
          >
            <svg width={800} height={400}>
              <rect width={800} height={400} fill={alpha(visxColors.primary, 0.02)} />
              
              {/* Scatter points */}
              {Array.from({ length: 20 }, (_, i) => ({
                x: 100 + Math.random() * 600,
                y: 50 + Math.random() * 300,
                value: Math.random() * 100,
              })).map((point, i) => (
                <circle
                  key={i}
                  cx={point.x}
                  cy={point.y}
                  r={6}
                  fill={visxColors.chart[i % visxColors.chart.length]}
                  opacity={0.7}
                />
              ))}

              {/* Annotations */}
              <Annotation
                x={250}
                y={150}
                dx={80}
                dy={-50}
              >
                <Connector stroke={visxColors.danger} strokeWidth={2} />
                <CircleSubject
                  radius={30}
                  stroke={visxColors.danger}
                  strokeWidth={2}
                  fill="transparent"
                />
                <Label
                  title="Peak Engagement"
                  subtitle="95% active"
                  backgroundFill="white"
                  backgroundPadding={8}
                  titleFontSize={13}
                  titleFontWeight={700}
                  subtitleFontSize={11}
                  width={140}
                  showAnchorLine={false}
                />
              </Annotation>

              <Annotation
                x={550}
                y={280}
                dx={-60}
                dy={-80}
              >
                <Connector stroke={visxColors.success} strokeWidth={2} />
                <CircleSubject
                  radius={25}
                  stroke={visxColors.success}
                  strokeWidth={2}
                  fill={alpha(visxColors.success, 0.1)}
                />
                <Label
                  title="Conversion Point"
                  subtitle="High ROI"
                  backgroundFill={alpha(visxColors.success, 0.1)}
                  backgroundPadding={8}
                  titleFontSize={13}
                  titleFontWeight={700}
                  subtitleFontSize={11}
                  width={130}
                  showAnchorLine={false}
                />
              </Annotation>

              <Annotation
                x={420}
                y={100}
                dx={70}
                dy={60}
              >
                <Connector stroke={visxColors.warning} strokeWidth={2} />
                <CircleSubject
                  radius={20}
                  stroke={visxColors.warning}
                  strokeWidth={2}
                  fill="transparent"
                />
                <Label
                  title="Attention Needed"
                  subtitle="Low engagement"
                  backgroundFill="white"
                  backgroundPadding={8}
                  titleFontSize={13}
                  titleFontWeight={700}
                  subtitleFontSize={11}
                  width={140}
                  showAnchorLine={false}
                />
              </Annotation>
            </svg>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box sx={{ mt: 6, p: 4, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: visxColors.primary }}>
          🎨 Visx by Airbnb
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Low-level visualization primitives for React. Build custom, production-ready data visualizations with D3 power and React composability.
          This showcase includes comparable charts to Recharts + Visx-unique advanced visualizations.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
          <Chip label="Production Ready" size="small" color="primary" />
          <Chip label="Fully Composable" size="small" sx={{ bgcolor: visxColors.success, color: 'white' }} />
          <Chip label="TypeScript Support" size="small" sx={{ bgcolor: visxColors.purple, color: 'white' }} />
          <Chip label="Used by Airbnb" size="small" sx={{ bgcolor: visxColors.warning, color: 'white' }} />
          <Chip label="D3 Powered" size="small" sx={{ bgcolor: visxColors.secondary, color: 'white' }} />
        </Box>
      </Box>
    </Box>
  );
};

export default VisxShowcase;
